<template>
  <Modal
    ref="modal"
    class="license-modal-container"
    :show-close-button="showCloseButton"
    @click-close="onClickClose"
  >
    <Loading
      v-if="state === 'loading'"
      message="Loading survey..."
    />
    <Loading
      v-if="state === 'submit'"
      message="Updating your product..."
    />
    <Success
      v-if="state === 'success'"
      @close="onCancel"
    />
    <Error
      v-if="state === 'error'"
      message="There's been an issue recording your response"
      @close="onCancel"
    />
    <div
      v-if="state === 'loaded'"
      class="survey-modal-container"
    >
      <div class="survey-modal-body">
        <keep-alive>
          <Question
            :question="question"
            :current-step="currentStep"
            :checked-options="checkedOptions"
            :progress="progress"
            @selectOption="onSelectOption"
          />
        </keep-alive>
      </div>
      <div class="survey-modal-footer">
        <Button
          v-if="survey.questions && currentStep < survey.questions.length - 1"
          class="continue-button"
          :disabled="!nextStepEnabled"
          :color="'primary'"
          @click="onContinue"
        >
          {{ nextStep }}
        </Button>
        <Button
          v-if="isSkipAllowed && canSkipQuestion && !isLastQuestion"
          class="cancel-button"
          color="primary"
          plain
          @click="onSkip"
        >
          Skip Question
        </Button>
        <Button
          v-if="isLastQuestion"
          class="complete-button"
          :disabled="!nextStepEnabled"
          :color="'warning'"
          @click="onContinue"
        >
          {{ nextStep }}
        </Button>
      </div>
    </div>
  </Modal>
</template>

<script>
import Modal from '../utilities/Modal.vue';
import Button from '../ui/Button.vue';
import Loading from '../ui/Loading.vue';
import Error from '../ui/Error.vue';
import Success from '../license/Success.vue';

import Question from './Question.vue';
import * as surveyService from '../../services/surveyService';

export default {
  components: {
    Modal,
    Button,
    Question,
    Loading,
    Success,
    Error,
  },
  emits: ['click-close'],
  props: {
    campaignId: {
      type: Number,
      required: true,
    },
    completeSurvey: {
      type: Function,
      required: true,
    },
    previousResponses: {
      type: Array,
      required: false,
      default: null,
    },
    isSkipAllowed: {
      type: Boolean,
      required: false,
      default: true,
    },
    showCloseButton: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  data() {
    return {
      state: 'loading',
      currentStep: 0,
      nextStep: 'Next',
      stepTitles: [],
      survey: {},
      question: null,
      responses: [],
      checkedOptions: (this.previousResponses && this.previousResponses.length)
        ? this.previousResponses[0].responses : [],
      progress: '0%',
    };
  },
  computed: {
    mobileTitle() {
      return this.stepTitles[this.currentStep];
    },
    canSkipQuestion() {
      return this.question && !this.question.required;
    },
    nextStepEnabled() {
      if (this.isLastQuestion || (this.question.question_type === 'single' && this.checkedOptions)) {
        return true;
      }
      return this.question.question_type === 'multiple' && this.checkedOptions.length >= 1;
    },
    isLastQuestion() {
      return this.currentStep === this.survey.questions.length - 1;
    },
  },
  watch: {
  },
  created() {
    this.fetchSurvey();
  },
  methods: {
    fetchSurvey() {
      surveyService.get(this.campaignId)
        .then((response) => {
          const surveyCampaign = response.data.survey_campaign;
          if (surveyCampaign) {
            this.survey = {
              id: surveyCampaign.id,
              questions: surveyCampaign.questions.sort((a, b) => a.sort - b.sort),
            };
            this.question = this.survey.questions[this.currentStep];
            this.stepTitles = this.survey.questions.map((q) => q.title);
            this.state = 'loaded';
          }
        })
        .catch(() => {
          this.onClickClose();
        });
    },
    selectStep(step) {
      this.currentStep = step;
      this.question = this.survey.questions[this.currentStep];
      this.checkedOptions = this.question.question_type === 'single' ? '' : [];
      if (this.previousResponses && this.previousResponses[this.currentStep]) {
        this.checkedOptions = this.question.question_type === 'single'
          ? this.previousResponses[this.currentStep].responses[0]
          : this.previousResponses[this.currentStep].responses;
      }
      if (this.currentStep === this.survey.questions.length - 1) {
        this.nextStep = 'Complete Survey';
      } else {
        this.nextStep = 'Next';
      }
    },
    onSelectOption(checkedOptions) {
      this.checkedOptions = checkedOptions;
    },
    emitResponse() {
      const responses = Array.isArray(this.checkedOptions) ? this.checkedOptions
        : [this.checkedOptions];
      const data = {
        question: this.question.id,
        responses,
        isLastQuestion: this.isLastQuestion,
      };
      this.$emit('updateResponse', data);
    },
    onStepChange(step) {
      if (this.currentStep === this.survey.questions.length - 1) {
        this.onClickClose();
      } else {
        this.selectStep(step);
      }
    },
    onSkip() {
      const responses = [];
      const data = {
        question: this.question.id,
        responses,
        isLastQuestion: this.isLastQuestion,
      };
      this.$emit('updateResponse', data);
      this.toNextStep();
    },
    toNextStep() {
      const curr = this.currentStep;
      if (curr === this.survey.questions.length - 1) {
        this.onClickClose();
      } else {
        this.selectStep(curr + 1);
        this.updateProgress();
      }
    },
    onCancel() {
      this.emitResponse();
      this.toNextStep();
    },
    onContinue() {
      this.emitResponse();
      const curr = this.currentStep;
      if (this.currentStep === this.survey.questions.length - 1) {
        this.completeSurvey();
      } else {
        this.selectStep(curr + 1);
        this.updateProgress();
        // reset the check box element styling especially for mobile views
        // the selector method will return a dynamically updated list, making a copy to avoid
        // concurrent modification
        const labelList = document.getElementsByClassName('question-option checked');
        while (labelList.length > 0) {
          labelList[0].className = 'question-option';
        }
      }
    },
    updateProgress() {
      // update progress bar relative to questions.length
      const percent = (this.currentStep * 100) / this.survey.questions.length;
      this.progress = `${percent}%`;
    },
    onClickClose() {
      this.$emit('click-close');
    },
  },
};
</script>

<style lang="scss">
  @import "../variables.scss";

  .modal-container{
    width: 600px;
    height: 542px;
  }
  .modal-close-button{
    width: 14px !important;
    height: 14px !important;
    top: 30px !important;
    right: 30px !important;
    border: none;
    padding: 0px !important;
    background-color: transparent;
    color: #0275D8;
  }
  .modal-body{
    box-shadow: none;
  }
  .modal-close-button::before, .modal-close-button::after{
    background-color: #0275D8 !important;
    height: 14px;
  }

  @media screen and (max-width: $license-modal-mobile) {
    .modal-close-button{
      top: 20px !important;
      right: 20px !important;
    }
  }
</style>

<style lang="scss" scoped>
  @import "../variables.scss";

  .survey-modal-container {
    padding: 30px;
    box-sizing: border-box;
    font-family: "Metropolis", sans-serif;
    display: flex;
    flex-direction: column;
    flex: 1;
  }
  .survey-modal-footer {
    position: absolute;
    bottom: 30px;
    right: 30px;
    left: 30px;
    border-top: 1px solid #CCCCCC;
    padding-top: 20px;
    margin-top: 30px;
  }
  .continue-button {
    padding: 0px;
    width: 93px;
    max-width: 93px;
    background-color: #0275D8;
    border-radius: 3px;
    margin-left: 30px;
    box-sizing: border-box;
  }
  .cancel-button, .continue-button {
    height: 41px;
    max-height: 41px;
    float: right;
  }
  .complete-button {
    width: 100%;
    height: 41px;
    max-width: 100%;
    max-height: 41px;
    padding: 0px;
  }
  .btn.cancel-button.primary.plain {
    font-weight: 300;
  }

  @media screen and (min-width: 800px) {
    .survey-modal-container {
      width: 100%;
    }
  }

  @media screen and (max-width: $license-modal-mobile) {
    .survey-modal-container {
      padding: 20px;
    }
    .survey-modal-footer {
      position: relative;
      text-align: center;
      left: 0px;
      bottom: 0px;
      right: 0px;
    }
    .continue-button{
      width: 100%;
      max-width: 100%;
      margin-left: 0rem;
      margin-bottom: 5px;
    }
    .cancel-button, .continue-button {
      float: none;
    }
    .cancel-button {
      margin-bottom: 5px;
    }
  }
</style>
